import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { Receipt, Upload, Plus, Trash2, Save, Send, X, FileText, Car, MapPin, Calendar, AlertCircle, Info, TrendingUp } from 'lucide-react';
import { formatCurrencyAmount } from '../components/CurrencyDisplay';

export default function ExpenseSubmit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Claim type selector
  const [claimType, setClaimType] = useState('general'); // 'general' or 'mileage'

  // ==================== GENERAL EXPENSE STATE ====================
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [purpose, setPurpose] = useState('');
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [subTotal, setSubTotal] = useState('');
  const [vatAmount, setVatAmount] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [glAccountId, setGlAccountId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [costCentre, setCostCentre] = useState('');
  const [uploadQueue, setUploadQueue] = useState([]);

  // ==================== MILEAGE EXPENSE STATE ====================
  const [trackerStatus, setTrackerStatus] = useState(null);
  const [loadingTracker, setLoadingTracker] = useState(true);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptOCRData, setReceiptOCRData] = useState(null);
  const [trips, setTrips] = useState([]);
  const [editingTrip, setEditingTrip] = useState(null);
  const [showTripForm, setShowTripForm] = useState(false);
  const [tripDate, setTripDate] = useState(new Date().toISOString().split('T')[0]);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [distance, setDistance] = useState('');
  const [tripPurpose, setTripPurpose] = useState('');
  const [returnTrip, setReturnTrip] = useState(false);
  const [vehicleType, setVehicleType] = useState('Car');

  // ==================== REFERENCE DATA ====================
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    if (claimType === 'mileage' && user?.userId) {
      fetchMileageTracker();
    }
  }, [claimType, user?.userId]);

  useEffect(() => {
    // Auto-calculate total for general expenses
    if (claimType === 'general') {
      const sub = parseFloat(subTotal) || 0;
      const vat = parseFloat(vatAmount) || 0;
      setTotalAmount((sub + vat).toFixed(2));
    }
  }, [subTotal, vatAmount, claimType]);

  const fetchReferenceData = async () => {
    try {
      const [catRes, deptRes, glRes, projRes, currRes] = await Promise.allSettled([
        apiClient.get('/expenses/categories/list'),
        apiClient.get('/departments'),
        apiClient.get('/gl-accounts'),
        apiClient.get('/projects'),
        apiClient.get('/currencies')
      ]);

      // Log individual failures but don't block the form from loading
      [catRes, deptRes, glRes, projRes, currRes].forEach((r, i) => {
        if (r.status === 'rejected') console.error('Failed to fetch reference data:', r.reason);
      });

      setCategories(catRes.status === 'fulfilled' ? (catRes.value.data.categories || []) : []);
      setDepartments(deptRes.status === 'fulfilled' ? (deptRes.value.data.departments || []) : []);
      setGlAccounts(glRes.status === 'fulfilled' ? (glRes.value.data.glAccounts || glRes.value.data || []) : []);
      setProjects(projRes.status === 'fulfilled' ? (projRes.value.data.projects || projRes.value.data || []) : []);
      setCurrencies(currRes.status === 'fulfilled' ? (currRes.value.data.currencies || currRes.value.data || []) : []);

      // Set default department
      const userDeptId = user?.departmentId || user?.DepartmentID;
      if (userDeptId) {
        setDepartmentId(userDeptId);
      }
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const fetchMileageTracker = async () => {
    const uid = user?.userId || user?.UserID;
    if (!uid) return;
    try {
      setLoadingTracker(true);
      const response = await apiClient.get(`/expenses/mileage-tracker/${uid}`);
      setTrackerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch mileage tracker:', error);
    } finally {
      setLoadingTracker(false);
    }
  };

  // ==================== GENERAL EXPENSE HANDLERS ====================
  const handleCategoryChange = (catId) => {
    setCategory(catId);
    const selectedCategory = categories.find(c => c.ExpenseCategoryID === catId);
    if (selectedCategory && selectedCategory.DefaultGLAccountID) {
      setGlAccountId(selectedCategory.DefaultGLAccountID);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      for (const file of files) {
        const preview = URL.createObjectURL(file);
        setUploadQueue(prev => [...prev, {
          file,
          preview,
          name: file.name,
          size: file.size,
          uploading: false,
          uploaded: false,
          blobURL: null
        }]);
      }
    } catch (error) {
      console.error('File selection error:', error);
      alert('Failed to select files');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveDocument = (index) => {
    setUploadQueue(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPendingDocuments = async (expenseId) => {
    const uploadedDocs = [];

    for (let i = 0; i < uploadQueue.length; i++) {
      const doc = uploadQueue[i];
      if (doc.uploaded) {
        uploadedDocs.push(doc);
        continue;
      }

      try {
        setUploadQueue(prev => prev.map((d, idx) => 
          idx === i ? { ...d, uploading: true } : d
        ));

        const formData = new FormData();
        formData.append('file', doc.file);
        formData.append('expenseId', expenseId);
        formData.append('documentType', 'Receipt');

        const response = await apiClient.post('/expenses/documents', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const blobURL = response.data?.blobURL || '';

        setUploadQueue(prev => prev.map((d, idx) => 
          idx === i ? { ...d, uploading: false, uploaded: true, blobURL } : d
        ));

        uploadedDocs.push({ ...doc, blobURL });
      } catch (error) {
        console.error(`Failed to upload ${doc.name}:`, error);
        setUploadQueue(prev => prev.map((d, idx) => 
          idx === i ? { ...d, uploading: false } : d
        ));
        throw new Error(`Failed to upload ${doc.name}`);
      }
    }

    return uploadedDocs;
  };

  // ==================== MILEAGE EXPENSE HANDLERS ====================
  const handleReceiptSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));

    // Auto-trigger OCR immediately on file/camera selection
    setUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', 'Receipt');

      const response = await apiClient.post('/expenses/documents/ocr', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setReceiptOCRData(response.data);
      console.log('Receipt OCR result:', response.data);
    } catch (error) {
      console.error('Failed to process receipt:', error);
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleUploadReceipt = async () => {}; // kept for reference; OCR now fires automatically on select

  const handleAddTrip = () => {
    if (!fromLocation || !toLocation || !distance || !tripPurpose) {
      alert('Please fill in all trip details');
      return;
    }

    const totalDistance = parseFloat(distance) * (returnTrip ? 2 : 1);
    
    const newTrip = {
      id: Date.now(),
      date: tripDate,
      fromLocation,
      toLocation,
      distance: totalDistance,
      purpose: tripPurpose,
      returnTrip,
      estimatedAmount: totalDistance * (trackerStatus?.currentRate || 0.55)
    };

    if (editingTrip !== null) {
      const updatedTrips = [...trips];
      updatedTrips[editingTrip] = newTrip;
      setTrips(updatedTrips);
      setEditingTrip(null);
    } else {
      setTrips([...trips, newTrip]);
    }

    // Reset form
    setFromLocation('');
    setToLocation('');
    setDistance('');
    setTripPurpose('');
    setReturnTrip(false);
    setTripDate(new Date().toISOString().split('T')[0]);
    setShowTripForm(false);
  };

  const handleEditTrip = (index) => {
    const trip = trips[index];
    setFromLocation(trip.fromLocation);
    setToLocation(trip.toLocation);
    setDistance(trip.returnTrip ? trip.distance / 2 : trip.distance);
    setTripPurpose(trip.purpose);
    setReturnTrip(trip.returnTrip);
    setTripDate(trip.date);
    setEditingTrip(index);
    setShowTripForm(true);
  };

  const handleDeleteTrip = (index) => {
    if (confirm('Delete this trip?')) {
      setTrips(trips.filter((_, i) => i !== index));
    }
  };

  const handleCancelEdit = () => {
    setFromLocation('');
    setToLocation('');
    setDistance('');
    setTripPurpose('');
    setReturnTrip(false);
    setTripDate(new Date().toISOString().split('T')[0]);
    setEditingTrip(null);
    setShowTripForm(false);
  };

  const getTotalMiles = () => trips.reduce((sum, trip) => sum + trip.distance, 0);
  const getTotalAmount = () => trips.reduce((sum, trip) => sum + trip.estimatedAmount, 0);

  const getTrackerStatusColor = () => {
    if (!trackerStatus || !trackerStatus.hasTracker) return 'bg-gray-100';
    
    const remaining = trackerStatus.milesRemaining || 0;
    if (remaining > 2000) return 'bg-green-100';
    if (remaining > 500) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  // ==================== SUBMIT HANDLERS ====================
  const handleSaveDraft = async () => {
    if (!purpose.trim() || !departmentId || !totalAmount || parseFloat(totalAmount) <= 0) {
      alert('Please fill in purpose, department, and amount');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/expenses', {
        expenseDate,
        currency,
        subTotal: parseFloat(subTotal) || 0,
        vatAmount: parseFloat(vatAmount) || 0,
        totalAmount: parseFloat(totalAmount),
        departmentId,
        glAccountId: glAccountId || null,
        projectId: projectId || null,
        costCentre: costCentre || null,
        purpose,
        category,
        notes,
        submitForApproval: false
      });

      const expenseId = response.data.expenseId;

      if (uploadQueue.length > 0) {
        try {
          await uploadPendingDocuments(expenseId);
        } catch (error) {
          console.error('Document upload failed:', error);
          alert('Expense saved but some documents failed to upload. You can upload them later.');
        }
      }

      alert('Expense saved as draft');
      navigate(`/expenses/${expenseId}`);
    } catch (error) {
      console.error('Save draft error:', error);
      alert(error.response?.data?.message || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitGeneral = async () => {
    if (!purpose.trim() || !departmentId || !totalAmount || parseFloat(totalAmount) <= 0) {
      alert('Please fill in purpose, department, and amount');
      return;
    }

    if (uploadQueue.length === 0) {
      const confirmSubmit = window.confirm('No receipts attached. Submit anyway?');
      if (!confirmSubmit) return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/expenses', {
        expenseDate,
        currency,
        subTotal: parseFloat(subTotal) || 0,
        vatAmount: parseFloat(vatAmount) || 0,
        totalAmount: parseFloat(totalAmount),
        departmentId,
        glAccountId: glAccountId || null,
        projectId: projectId || null,
        costCentre: costCentre || null,
        purpose,
        category,
        notes,
        submitForApproval: true
      });

      const expenseId = response.data.expenseId;

      if (uploadQueue.length > 0) {
        try {
          await uploadPendingDocuments(expenseId);
        } catch (error) {
          console.error('Document upload failed:', error);
          alert('Expense submitted but some documents failed to upload. You can upload them from the expense detail page.');
        }
      }

      alert('Expense submitted for approval!');
      navigate(`/expenses/${expenseId}`);
    } catch (error) {
      console.error('Submit expense error:', error);
      alert(error.response?.data?.message || 'Failed to submit expense');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMileage = async (e) => {
    e.preventDefault();

    if (trips.length === 0) {
      alert('Please add at least one trip');
      return;
    }

    if (!departmentId) {
      alert('Please select a department');
      return;
    }

    if (!purpose?.trim()) {
      alert('Please enter an overall purpose for this mileage claim');
      return;
    }

    setSubmitting(true);

    try {
      const totalAmount = trips.reduce((sum, trip) => sum + trip.estimatedAmount, 0);
      
      const headerData = {
        expenseDate: trips[0].date,
        purpose: purpose,
        category: 'Mileage',
        currency: 'GBP',
        departmentId,
        totalAmount,
        subTotal: totalAmount,
        vatAmount: receiptOCRData?.vatDetails?.vatAmount || 0,
        notes: receiptOCRData ? `Fuel receipt: ${receiptOCRData.fuelData?.merchantName || 'Unknown'}` : undefined,
        submitForApproval: true
      };

      const headerResponse = await apiClient.post('/expenses', headerData);
      const expenseHeaderId = headerResponse.data.expenseId;

      // Upload receipt document if we have OCR data
      let documentId = null;
      if (receiptFile && receiptOCRData) {
        const docFormData = new FormData();
        docFormData.append('file', receiptFile);
        docFormData.append('expenseId', expenseHeaderId);
        docFormData.append('documentType', 'Receipt');

        const docResponse = await apiClient.post('/expenses/documents', docFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        documentId = docResponse.data.documentId;
      }

      // Submit each trip
      let warnings = [];
      for (const trip of trips) {
        const mileageData = {
          expenseHeaderId,
          fromLocation: trip.fromLocation,
          toLocation: trip.toLocation,
          distance: trip.distance,
          vehicleType,
          mileageDate: trip.date,
          purpose: trip.purpose,
          returnTrip: trip.returnTrip,
          receiptDocumentId: documentId
        };

        const mileageResponse = await apiClient.post('/expenses/mileage', mileageData);
        
        if (mileageResponse.data.warning) {
          warnings.push(mileageResponse.data.warning);
        }
      }

      // Create VAT claim if applicable
      if (receiptOCRData?.vatDetails && documentId) {
        try {
          await apiClient.post('/expenses/vat-claims/create', {
            expenseDocumentId: documentId,
            expenseId: expenseHeaderId,
            vatAmount: receiptOCRData.vatDetails.vatAmount,
            netAmount: receiptOCRData.vatDetails.netAmount,
            grossAmount: receiptOCRData.vatDetails.grossAmount,
            receiptDate: receiptOCRData.fuelData?.date || trips[0].date,
            merchantName: receiptOCRData.fuelData?.merchantName,
            fuelType: receiptOCRData.fuelData?.fuelType,
            litres: receiptOCRData.fuelData?.litres
          });
        } catch (vatError) {
          console.error('Failed to create VAT claim:', vatError);
        }
      }

      await fetchMileageTracker();

      let message = `Mileage claim with ${trips.length} trip(s) submitted for approval!`;
      if (warnings.length > 0) {
        message += '\n\n⚠️ ' + warnings.join('\n');
      }
      if (receiptOCRData?.vatDetails?.vatAmount) {
        message += `\n\n✅ VAT claim created: £${receiptOCRData.vatDetails.vatAmount}`;
      }

      alert(message);
      navigate('/expenses');
    } catch (error) {
      console.error('Failed to create mileage expense:', error);
      alert(error.response?.data?.message || 'Failed to create mileage expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Receipt className="w-7 h-7 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Submit Expense</h1>
              <p className="text-sm text-gray-500">Choose the type of expense and fill in the details</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/expenses')}
            className="text-gray-600 hover:text-gray-900"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Claim Type Selector */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Claim Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setClaimType('general')}
            className={`p-6 border-2 rounded-lg text-left transition-all ${
              claimType === 'general'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Receipt className={`w-8 h-8 ${claimType === 'general' ? 'text-blue-600' : 'text-gray-400'}`} />
              <h3 className="text-lg font-semibold text-gray-900">General Expense</h3>
            </div>
            <p className="text-sm text-gray-600">
              Meals, travel, accommodation, office supplies, and other business expenses with receipts
            </p>
          </button>

          <button
            type="button"
            onClick={() => setClaimType('mileage')}
            className={`p-6 border-2 rounded-lg text-left transition-all ${
              claimType === 'mileage'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Car className={`w-8 h-8 ${claimType === 'mileage' ? 'text-blue-600' : 'text-gray-400'}`} />
              <h3 className="text-lg font-semibold text-gray-900">Mileage Claim</h3>
            </div>
            <p className="text-sm text-gray-600">
              Business mileage with fuel receipt OCR, VAT tracking, and HMRC-compliant rates
            </p>
          </button>
        </div>
      </div>

      {/* GENERAL EXPENSE FORM */}
      {claimType === 'general' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Purpose / Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    placeholder="e.g., Client meeting lunch, Conference registration, Taxi to airport"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expense Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={expenseDate}
                    onChange={(e) => setExpenseDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat.ExpenseCategoryID} value={cat.CategoryName}>
                        {cat.CategoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes or context..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="2"
                  />
                </div>
              </div>
            </div>

            {/* Financial Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Details</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="GBP">GBP - British Pound</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    {currencies.filter(c => !['GBP', 'USD', 'EUR'].includes(c.CurrencyCode)).map(curr => (
                      <option key={curr.CurrencyCode} value={curr.CurrencyCode}>
                        {curr.CurrencyCode} - {curr.CurrencyName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Net Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={subTotal}
                    onChange={(e) => setSubTotal(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VAT Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={vatAmount}
                    onChange={(e) => setVatAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={totalAmount}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-semibold"
                  />
                </div>
              </div>
            </div>

            {/* Allocation */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Allocation</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select department...</option>
                    {departments.map(dept => (
                      <option key={dept.DepartmentID} value={dept.DepartmentID}>
                        {dept.DepartmentName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GL Account
                  </label>
                  <select
                    value={glAccountId}
                    onChange={(e) => setGlAccountId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select GL account...</option>
                    {glAccounts.map(gl => (
                      <option key={gl.GLAccountID} value={gl.GLAccountID}>
                        {gl.AccountCode} - {gl.AccountName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project (Optional)
                  </label>
                  <select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select project...</option>
                    {projects.map(proj => (
                      <option key={proj.ProjectID} value={proj.ProjectID}>
                        {proj.ProjectCode} - {proj.ProjectName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost Centre (Optional)
                  </label>
                  <input
                    type="text"
                    value={costCentre}
                    onChange={(e) => setCostCentre(e.target.value)}
                    placeholder="e.g., CC-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar for General Expenses */}
          <div className="space-y-6">
            {/* Receipts Upload */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Receipts
              </h2>

              <div className="mb-4">
                <label className="block w-full">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors">
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <div className="text-sm text-gray-600">
                      Click to upload receipts
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      PDF, JPG, PNG up to 10MB
                    </div>
                  </div>
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    className="hidden"
                  />
                </label>
              </div>

              {uploadQueue.length > 0 && (
                <div className="space-y-2">
                  {uploadQueue.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {doc.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(doc.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveDocument(index)}
                        className="ml-2 text-red-600 hover:text-red-800"
                        disabled={doc.uploading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="bg-blue-50 rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Net Amount:</span>
                  <span className="font-medium">{formatCurrencyAmount(subTotal || 0, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">VAT:</span>
                  <span className="font-medium">{formatCurrencyAmount(vatAmount || 0, currency)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between text-base">
                  <span className="font-semibold">Total:</span>
                  <span className="font-bold text-blue-600">
                    {formatCurrencyAmount(totalAmount || 0, currency)}
                  </span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="text-gray-600">Receipts:</span>
                  <span className="font-medium">{uploadQueue.length}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow p-6 space-y-3">
              <button
                onClick={handleSubmitGeneral}
                disabled={loading || uploading}
                className="w-full inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Send className="w-5 h-5 mr-2" />
                {loading ? 'Submitting...' : 'Submit for Approval'}
              </button>

              <button
                onClick={handleSaveDraft}
                disabled={loading || uploading}
                className="w-full inline-flex items-center justify-center px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Save className="w-5 h-5 mr-2" />
                Save as Draft
              </button>

              <button
                onClick={() => navigate('/expenses')}
                disabled={loading || uploading}
                className="w-full inline-flex items-center justify-center px-4 py-3 text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MILEAGE EXPENSE FORM */}
      {claimType === 'mileage' && (
        <div className="max-w-[1400px] mx-auto space-y-6">
          {/* Mileage Tracker Status */}
          {!loadingTracker && trackerStatus && trackerStatus.hasTracker && (
            <div className={`rounded-lg shadow p-6 ${getTrackerStatusColor()}`}>
              <div className="flex items-start gap-4">
                <TrendingUp className="w-6 h-6 text-gray-700 mt-1" />
                <div className="flex-1">
                  <h2 className="font-semibold text-gray-900 mb-2">
                    Personal Mileage — Tax Year {trackerStatus.taxYear}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Total Miles</p>
                      <p className="font-bold text-lg">{trackerStatus.totalMilesSubmitted?.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Standard Rate Miles</p>
                      <p className="font-bold text-lg">{trackerStatus.standardRateMiles?.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Reduced Rate Miles</p>
                      <p className="font-bold text-lg">{trackerStatus.reducedRateMiles?.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Current Rate</p>
                      <p className="font-bold text-lg">£{trackerStatus.currentRate}/mile</p>
                    </div>
                  </div>
                  {trackerStatus.warning && (
                    <div className="mt-3 flex items-start gap-2 text-orange-800">
                      <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <p className="text-sm font-medium">{trackerStatus.warning}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmitMileage} className="space-y-6">
            {/* Step 1: Upload Fuel Receipt */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <h3 className="text-lg font-semibold text-gray-900">Upload Fuel Receipt (Optional)</h3>
              </div>

              <p className="text-sm text-gray-600">
                Upload your fuel receipt to track VAT and link multiple trips to the same fuel purchase
              </p>

              <div className="flex gap-4 items-start">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Receipt Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleReceiptSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                {uploadingReceipt && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-md text-sm">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    Scanning receipt...
                  </div>
                )}
              </div>

              {/* Receipt Preview */}
              {receiptPreview && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Receipt Preview</p>
                    <img src={receiptPreview} alt="Receipt" className="w-full h-48 object-contain border rounded" />
                  </div>

                  {/* OCR Data Display */}
                  {receiptOCRData && receiptOCRData.isFuelReceipt && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Fuel Receipt Detected
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date:</span>
                          <span className="font-medium">{receiptOCRData.fuelData?.date ? new Date(receiptOCRData.fuelData.date).toLocaleDateString('en-GB') : '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Merchant:</span>
                          <span className="font-medium">{receiptOCRData.fuelData?.merchantName || 'Unknown'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fuel Type:</span>
                          <span className="font-medium">{receiptOCRData.fuelData?.fuelType || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Litres:</span>
                          <span className="font-medium">{receiptOCRData.fuelData?.litres || '-'}L</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Cost:</span>
                          <span className="font-medium">£{receiptOCRData.fuelData?.totalCost || 0}</span>
                        </div>
                        {receiptOCRData.vatDetails && (
                          <div className="flex justify-between border-t pt-2 mt-2">
                            <span className="text-gray-600">VAT Claimable:</span>
                            <span className="font-bold text-green-700">£{receiptOCRData.vatDetails.vatAmount || 0}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Add Trips */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                  <h3 className="text-lg font-semibold text-gray-900">Add Mileage Trips</h3>
                </div>

                {!showTripForm && (
                  <button
                    type="button"
                    onClick={() => setShowTripForm(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Add Trip
                  </button>
                )}
              </div>

              {/* Trip Form */}
              {showTripForm && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Location *
                      </label>
                      <input
                        type="text"
                        value={fromLocation}
                        onChange={(e) => setFromLocation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Home"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        To Location *
                      </label>
                      <input
                        type="text"
                        value={toLocation}
                        onChange={(e) => setToLocation(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Client Office"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Distance (miles) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={distance}
                        onChange={(e) => setDistance(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="25"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={tripDate}
                        onChange={(e) => setTripDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Purpose *
                      </label>
                      <input
                        type="text"
                        value={tripPurpose}
                        onChange={(e) => setTripPurpose(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Client meeting at Birmingham office"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="returnTrip"
                      checked={returnTrip}
                      onChange={(e) => setReturnTrip(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label htmlFor="returnTrip" className="text-sm font-medium text-gray-700">
                      Return trip (double the distance)
                    </label>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddTrip}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      {editingTrip !== null ? 'Update Trip' : 'Add Trip'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Trips List */}
              {trips.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900">Trips ({trips.length})</h4>
                  <div className="space-y-2">
                    {trips.map((trip, index) => (
                      <div key={trip.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{trip.fromLocation} → {trip.toLocation}</span>
                            {trip.returnTrip && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Return</span>}
                          </div>
                          <div className="text-sm text-gray-600">
                            {new Date(trip.date).toLocaleDateString('en-GB')} • {trip.distance} miles • {trip.purpose}
                          </div>
                          <div className="text-sm font-medium text-green-600 mt-1">
                            ~£{trip.estimatedAmount.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditTrip(index)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteTrip(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Trips Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Total Miles:</span>
                      <span className="font-bold">{getTotalMiles().toFixed(1)} miles</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="font-medium">Estimated Total:</span>
                      <span className="font-bold text-blue-700">£{getTotalAmount().toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      * Actual amounts will be calculated using the correct HMRC rates based on your tax year mileage
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: Final Details */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <h3 className="text-lg font-semibold text-gray-900">Final Details</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Overall Purpose / Description *
                  </label>
                  <textarea
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    placeholder="e.g., Customer site visit, Business trip to London, Sales meetings"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="2"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This describes the overall reason for your mileage claim. Individual trip purposes are optional.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vehicle Type *
                    </label>
                    <select
                      value={vehicleType}
                      onChange={(e) => setVehicleType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Car">Car</option>
                      <option value="Motorcycle">Motorcycle</option>
                      <option value="Bicycle">Bicycle</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department *
                    </label>
                    <select
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select department...</option>
                      {departments.map(dept => (
                        <option key={dept.DepartmentID} value={dept.DepartmentID}>
                          {dept.DepartmentName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={submitting || trips.length === 0}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                {submitting ? 'Submitting...' : `Submit ${trips.length} ${trips.length === 1 ? 'Trip' : 'Trips'}`}
              </button>

              <button
                type="button"
                onClick={() => navigate('/expenses')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
